'use client';

import { usePathname } from 'next/navigation';
import type { TiPremiumPrice } from '../../calculatedAttributes/providers/tiQuote';
import { useScopeCalculations } from '../../calculatedAttributes/store';
import type { CmsProduct } from '../../types/cmsProduct';
import type { PushDataLayerFn } from '../../types/pushDataLayer';
import {
  ComponentTypeEnum,
  configureTiSearchResultParameters,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics/sendAnalytics';
import { sessionAttributionTracker } from '../../utils/sessionAttributionTracking/sessionAttributionTracker';
import { resolveTrackingPrice } from './format';

/**
 * `component_type` per CTA, from the A&A dictionary (Airtable #2389 / #2391).
 * Both values are also used by unrelated components — the blog-article Spotlight
 * block and an unrelated CMS block respectively — and A&A confirmed on CMS-1795
 * that they are the intended values here regardless, so the overlap is expected.
 */
export const COMPONENT_TYPE = {
  tierCard: ComponentTypeEnum.MINI_PRODUCT_HIGHLIGHT,
  summaryPanel: ComponentTypeEnum.PRODUCT_SPOTLIGHT,
} as const;

// Static per the tracking plan, identical on both CTAs.
const COMPONENT_TITLE = 'Product Card/CTA_Apply Now';

export type PlanTierApplyTracking = (args: {
  product: CmsProduct;
  /** Tier position in the price-sorted list. */
  index: number;
  /** Absent while the premium resolves, and for a tier the settled quote did not price. */
  price: TiPremiumPrice | undefined;
  componentType: (typeof COMPONENT_TYPE)[keyof typeof COMPONENT_TYPE];
}) => void;

/**
 * Apply-Now click tracking for the two M3 CTAs (CMS-1795): the per-tier card and
 * the desktop summary panel. Both push `product_click_apply` and differ only in
 * `component_type`.
 *
 * `item_list_name` is the raw page path, as on the other product surfaces here.
 * Do not normalize it — the `/-cms/draft` prefix is what lets reporting tell
 * internal preview traffic apart from real customers (CMS-1795).
 */
export const usePlanTierApplyTracking = (
  pushDataLayer: PushDataLayerFn,
  currencyCode: string,
): PlanTierApplyTracking => {
  const itemListName = usePathname() ?? undefined;
  const { travelQuote: travelQuotesData } = useScopeCalculations();

  return ({ product, index, price, componentType }) => {
    sessionAttributionTracker.recordApplyClick();

    const searchResultParameter =
      configureTiSearchResultParameters(travelQuotesData);

    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: COMPONENT_TITLE,
      componentType,
      event: 'product_click_apply',
      index,
      itemListName,
      product,
      pushDataLayer,
      // An empty string would override the page-level value in GTM, so the key
      // is dropped instead when there are no search criteria.
      extraData: searchResultParameter
        ? { search_result_parameter: searchResultParameter }
        : {},
      itemExtraData: resolveTrackingPrice(price, currencyCode),
      // Approval odds are a credit-hero model: on TI the default
      // `aov-unavailable|aos-unavailable` would report a state that does not
      // exist for these products, so the key is dropped (CMS-1795).
      omitProductContext: true,
    });
  };
};
